'use client';
import { faTimes } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect } from 'react';
import { useAccount } from '@/hooks/useAccount';
import { getSourcePath } from '@/rewrites';
import { usePathname } from 'next/navigation';
import { useAuthenticated } from '@/hooks/useAuthenticated';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import Cookies from 'js-cookie';
import TrialInfo from './TrialInfo';
import ActivateTrialButton from './ActivateTrialButton';

interface TrialBannerProps {
  isSubscriptionVersion?: boolean;
}

const TrialBanner = ({ isSubscriptionVersion = false }: TrialBannerProps) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const { customer, loading } = useAccount();
  const { isAuthenticated, isLoading: authLoading } = useAuthenticated();
  const [trialBannerCookie, setTrialBannerCookie] = useState<string | undefined>(
    !isSubscriptionVersion ? Cookies.get('bidpilot_trial_banner_dismissed') : undefined,
  );
  const pathname = usePathname();
  const trialActivated = !!customer?.currentTrial?.activation;

  useEffect(() => {
    dayjs.extend(duration);
  }, []);

  useEffect(() => {
    if (authLoading || !isAuthenticated || loading) return;

    if (customer?.existingStripeSubscription) {
      setIsVisible(false);
      return;
    }

    if (isSubscriptionVersion) {
      setIsVisible(!!customer?.currentTrial);
    } else {
      if (pathname === getSourcePath('/subscription')) {
        setIsVisible(false);
        return;
      }

      const hasInactiveTrial = customer?.currentTrial ? !trialActivated : false;
      setIsVisible(!trialBannerCookie && hasInactiveTrial);
    }
  }, [authLoading, isAuthenticated, loading, customer, pathname, trialBannerCookie, isSubscriptionVersion]);

  const calculateDaysRemaining = (activationDateString: string, durationString: string): number => {
    const totalDays = dayjs.duration(durationString).asDays();
    const activationDate = dayjs(activationDateString);
    const endDate = activationDate.add(totalDays, 'day');
    const now = dayjs();

    return endDate.diff(now, 'day');
  };

  const getDaysLeft = (): number => {
    const trial = customer?.currentTrial;
    if (!trial || !trial.activation) return 0;

    return calculateDaysRemaining(trial.activation, trial.duration);
  };

  const getTrialDurationInDays = (duration: string): number => {
    const days = dayjs.duration(duration).asDays();
    return days < 1 ? 1 : Math.floor(days);
  };

  const handleDismiss = () => {
    setTrialBannerCookie(Cookies.set('bidpilot_trial_banner_dismissed', 'true', { expires: 7 }));
    setIsVisible(false);
  };

  if (!isAuthenticated || authLoading || !isVisible || loading || !customer?.currentTrial) {
    return null;
  }

  const trial = customer.currentTrial;
  const trialTier = trial.subscriptionTier;
  const trialDurationDays = getTrialDurationInDays(trial.duration);
  const daysLeft = getDaysLeft();

  const trialInfoProps = {
    isActive: trialActivated,
    trialTier,
    trialDurationDays,
    ...(trialActivated && { daysLeft }),
  };

  const bannerContent = <TrialInfo {...trialInfoProps} />;

  const actionButtons = (
    <div className="flex items-center space-x-2">
      {!trialActivated && <ActivateTrialButton isSubscriptionVersion={isSubscriptionVersion} />}
      {!isSubscriptionVersion && (
        <button
          onClick={handleDismiss}
          className="flex h-6 w-6 items-center justify-center text-xl font-bold text-white hover:text-gray-200"
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </div>
  );

  if (isSubscriptionVersion) {
    return (
      <div className="flex w-full items-center justify-between rounded-md bg-accent px-8 py-3 text-accent-content">
        {bannerContent}
        {actionButtons}
      </div>
    );
  }

  return (
    <div className="w-full bg-accent">
      <div className="page-layout flex w-full items-center justify-between px-8 py-3 text-accent-content">
        {bannerContent}
        {actionButtons}
      </div>
    </div>
  );
};

export default TrialBanner;
